import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { SkillWeight } from '../lesson/category/skill-weight'
import { AchievementConditionGroup } from './achievement-condition-group'

export const AchievementBadge = pgTable(
  'achievement_badge',
  {
    id: uuid().notNull().primaryKey(),
    title: text().notNull(),
    description: text(),
    badgeType: text({ enum: ['SPECIAL', 'RARE', 'BASIC'] }).notNull(),
    isActive: boolean().notNull().default(true),
    iconUrl: text(),
    bonusRewardEnabled: boolean().notNull().default(false),
    bonusCoreSkillTagId: uuid().references(() => SkillWeight.id, { onDelete: 'set null' }),
    bonusXpValue: integer(),
    groupCombineOperator: text({ enum: ['AND', 'OR'] }).default('AND'),
    createdAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }),
    legacyBadgeId: integer(),
  },
  (table) => [
    index('achievement_badge_title_idx').on(table.title),
    index('achievement_badge_badge_type_idx').on(table.badgeType),
    index('achievement_badge_is_active_idx').on(table.isActive),
    index('achievement_badge_icon_url_idx').on(table.iconUrl),
    index('achievement_badge_bonus_core_skill_tag_id_idx').on(table.bonusCoreSkillTagId),
    index('achievement_badge_created_at_idx').on(table.createdAt),
    index('achievement_badge_updated_at_idx').on(table.updatedAt),
    index('achievement_badge_legacy_badge_id_idx').on(table.legacyBadgeId),
  ],
)

export const achievementBadgeRelations = relations(AchievementBadge, ({ one, many }) => ({
  bonusCoreSkillTag: one(SkillWeight, {
    fields: [AchievementBadge.bonusCoreSkillTagId],
    references: [SkillWeight.id],
  }),
  conditionGroups: many(AchievementConditionGroup),
}))

export const InsertAchievementBadgeSchema = createInsertSchema(AchievementBadge)
export const UpdateAchievementBadgeSchema = createUpdateSchema(AchievementBadge)
  .omit({ id: true, legacyBadgeId: true })
  .partial()
export const SelectAchievementBadgeSchema = createSelectSchema(AchievementBadge)

export type InsertAchievementBadge = typeof AchievementBadge.$inferInsert
export type UpdateAchievementBadge = z.infer<typeof UpdateAchievementBadgeSchema>
export type SelectAchievementBadge = typeof AchievementBadge.$inferSelect
